import React, { PureComponent } from 'react';
import { ModalPanelProps } from '../../DashboardModal';
import {
    DatasetDto,
    FLIGHT_DETAILS_REQUEST_DTO_CLAZZ_VALUE,
    FlightDataset,
    FlightDetailsRequestDto,
    FlightDto,
    FlightExchangeModelType,
    FlightFieldType,
    FlightId,
    StandardFlightFieldType,
} from '../../../../model/CORE';
import { Flex } from '../../../common/Flex';
import InputTime from '../../../../common/input/InputTime';
import { Checkbox, Col, Input, notification, Row } from 'antd';
import { ServiceFactory } from '../../../../service/ServiceFactory';
import FlightUtils from '../../../../common/util/FlightUtils';
import TitledBorder from '../../../../common/panel/TitledBorder';
import FlightLevelUtils from '../../../../common/util/FlightLevelUtils';
import TimeUtils from '../../../../common/util/TimeUtils';
import Utils from '../../../../common/util/Utils';
import AirspaceUtils from '../../../../common/util/AirspaceUtils';
import './FlightDetailsPanel.less';
import FlightRegulationLocationTable, {
    StandardFlightRegulationLocationColumn,
} from '../../../table/FlightRegulationLocationTable';
import { SortMode } from '../../../../common/table/ManagedTable';
import KeyUtils from '../../../../common/util/KeyUtils';

export const DEFAULT_FLIGHT_FIELDS: Array<FlightFieldType> = [
    // Standard flight fields
    StandardFlightFieldType.AO,
    StandardFlightFieldType.FLIGHT_CRITICALITY,
    StandardFlightFieldType.AC_TYPE,
    StandardFlightFieldType.OPERATING_AO,
    StandardFlightFieldType.INITIAL_RFL,
    StandardFlightFieldType.ETOT,
    StandardFlightFieldType.CTOT,
    StandardFlightFieldType.ATOT,
    StandardFlightFieldType.ETA,
    StandardFlightFieldType.CTA,
    StandardFlightFieldType.ATA,
    StandardFlightFieldType.TAXI_TIME,
    StandardFlightFieldType.CURRENT_DEPARTURE_TAXI_TIME_AND_PROCEDURE,
    StandardFlightFieldType.CDM_EOBT,
    StandardFlightFieldType.READY_EOBT,
    StandardFlightFieldType.CFMU_FLIGHT_TYPE,
    StandardFlightFieldType.PROFILE_VALIDITY,
    StandardFlightFieldType.LATE_FILER,
    StandardFlightFieldType.LATE_UPDATER,
    StandardFlightFieldType.READY_STATUS,

    StandardFlightFieldType.REVISION_TIMES,
    StandardFlightFieldType.RUNWAY_VISUAL_RANGE,
    StandardFlightFieldType.FILED_REGISTRATION,
    StandardFlightFieldType.CDM_INFO,
    StandardFlightFieldType.LAST_ATFM_MESSAGE_TYPE,
    StandardFlightFieldType.ICAO_ROUTE,
    StandardFlightFieldType.EET,
    StandardFlightFieldType.ROUTE_LENGTH,
    StandardFlightFieldType.DELAY,
    StandardFlightFieldType.MPR,
    StandardFlightFieldType.REGULATION_LOCATIONS,
    StandardFlightFieldType.PROPOSAL_INFORMATION,
    StandardFlightFieldType.PROPOSAL_CONTEXT,
    StandardFlightFieldType.HAS_BEEN_FORCED,
    StandardFlightFieldType.MPR_CAUSE,
    StandardFlightFieldType.EXEMPTED_FROM_REGULATIONS,
    //StandardFlightFieldType.CTFM_POINT_PROFILE, StandardFlightFieldType.FTFM_POINT_PROFILE, StandardFlightFieldType.RTFM_POINT_PROFILE,
    //StandardFlightFieldType.CTFM_AIRSPACE_PROFILE, StandardFlightFieldType.FTFM_AIRSPACE_PROFILE, StandardFlightFieldType.RTFM_AIRSPACE_PROFILE,

    // STAM related
    //	StandardFlightFieldType.CAUGHT_IN_HOTSPOTS,StandardFlightFieldType.ATFCM_MEASURE_LOCATIONS,

    StandardFlightFieldType.ARRIVAL_INFORMATION,
];

export interface FlightDetailsPanelProps extends ModalPanelProps {
    id: FlightId;
    dataset: DatasetDto;
    proposalContext: string;
}

const INITIAL_STATE: FlightDetailsPanelState = {
    flightData: null,
};

export interface FlightDetailsPanelState {
    flightData: FlightDto;
}

export default class FlightDetailsPanel extends PureComponent<FlightDetailsPanelProps, FlightDetailsPanelState> {
    public static INITIAL_WIDTH = '100em';

    constructor(props) {
        super(props);
        this.state = INITIAL_STATE;
    }

    render() {
        return (
            <Flex direction="vertical" className="flight-details-panel overflow">
                {this.renderHeader()}
                {this.renderContent()}
            </Flex>
        );
    }

    componentDidMount() {
        this.doGetFlightDetails();
    }

    renderContent() {
        const flight = this.state.flightData;
        const data = this.state.flightData || {}; // Useful to prevent from doing null checks
        const cdmInfo = data.cdmInfo;
        const arrivalInformation = data.arrivalInformation;
        return (
            <Flex direction="vertical" className="generic" style={{ width: '100%' }}>
                <TitledBorder className="custom-titled titled-border">
                    <Row>
                        {field('AO', data.aircraftOperator, 2, 6)}
                        {field('Aircaft Type', data.aircraftType, 3, 5)}
                        {field('Registration Mark', data.filedRegistration, 3, 5)}
                    </Row>
                    <Row>
                        {field('OPR AO', data.operatingAircraftOperator, 2, 6)}
                        {field('Initial RFL', FlightLevelUtils.flightLevelDisplayValue(data.initialRfl), 3, 5)}
                        {field('RVR', data.runwayVisualRange, 3, 5)}
                    </Row>
                </TitledBorder>
                <TitledBorder title="TIME" className="custom-titled titled-border">
                    <Row>
                        {field('ETOT', data.estimatedTakeOffTime, 2, 4)}
                        {field(
                            'EET',
                            data.estimatedElapsedTimeInMs && data.estimatedElapsedTimeInMs / TimeUtils.MIN_TO_MS,
                            2,
                            3,
                        )}
                        {field('ETA', data.estimatedTimeOfArrival, 2, 4)}
                        {field('CDM EOBT', data.cdmEobt, 2, 5)}
                    </Row>
                    <Row>
                        {field('CTOT', data.calculatedTakeOffTime, 2, 4)}
                        {field('Taxi', data.taxiTimeInMs && data.taxiTimeInMs / TimeUtils.MIN_TO_MS, 2, 3)}
                        {field('CTA', data.calculatedTimeOfArrival, 2, 4)}
                        {field('SIP', data.proposalInformation && data.proposalInformation.proposedCtot, 2, 5)}
                    </Row>
                    <Row>
                        {field('ATOT', data.actualTakeOffTime, 2, 4)}
                        {field(
                            'Actual Taxi',
                            data.currentDepartureTaxiTimeAndProcedure &&
                                data.currentDepartureTaxiTimeAndProcedure.taxiTimeInMs / TimeUtils.MIN_TO_MS,
                            2,
                            3,
                        )}
                        {field('ATA', data.actualTimeOfArrival, 2, 4)}
                        {field(
                            'Last Valid',
                            data.profileValidity && FlightUtils.getLastValidityString(data.profileValidity),
                            2,
                            5,
                        )}
                    </Row>
                </TitledBorder>
                <TitledBorder title="STATUS" className="custom-titled titled-border">
                    <Row>
                        {field('Flight Type', data.cfmuFlightType, 3, 12)}
                        {field('Late Filer', flight && Utils.toBooleanValue(data.lateFiler), 3, 6)}
                    </Row>
                    <Row>
                        {field('Exempt Flight', flight && Utils.toBooleanValue(data.exemptedFromRegulations), 3, 12)}
                        {field('Late Updater', flight && Utils.toBooleanValue(data.lateUpdater), 3, 6)}
                    </Row>
                    <Row>
                        {field(
                            'RFI',
                            flight && Utils.toBooleanValue(data.readyStatus && data.readyStatus.readyForImprovement),
                            3,
                            12,
                        )}
                        {field(
                            'FCI',
                            data.flightCriticality && FlightUtils.toFlightCriticalityString(data.flightCriticality),
                            3,
                            6,
                        )}
                    </Row>
                    <Row>
                        {field(
                            'Ready To Depart',
                            flight && Utils.toBooleanValue(data.readyStatus && data.readyStatus.readyToDepart),
                            3,
                            12,
                        )}
                        {field(
                            'TIS/TRS',
                            data.revisionTimes &&
                                data.revisionTimes.timeToInsertInMs / TimeUtils.MIN_TO_MS +
                                    '/' +
                                    data.revisionTimes.timeToRemoveInMs / TimeUtils.MIN_TO_MS,
                            3,
                            6,
                        )}
                    </Row>
                </TitledBorder>
                <TitledBorder title="DEPARTURE (CDM)" className="custom-titled titled-border">
                    <Row>
                        {field('Status', cdmInfo?.status, 2, 8)}
                        {field('ATC TTOT', cdmInfo?.atcTtot, 3, 4)}
                        {field('Aircraft Type', cdmInfo?.aircraftType, 3, 4)}
                    </Row>
                    <Row>
                        {field('SID', AirspaceUtils.toTerminalProcedureOrDctString(cdmInfo?.sid), 2, 8)}
                        {field('Consolidated TTOT', cdmInfo?.consolidatedTtot, 3, 4)}
                        {field('Registration Mark', cdmInfo?.registration, 3, 4)}
                    </Row>
                    <Row>
                        {field(
                            'Taxi Time',
                            cdmInfo?.taxiTimeInMs >= 0 ? cdmInfo?.taxiTimeInMs / TimeUtils.MIN_TO_MS : undefined,
                            2,
                            8,
                        )}
                        {field('Earliest TTOT', cdmInfo?.earliestTtot, 3, 4)}
                        {field('TSAT', cdmInfo?.tsat, 3, 4)}
                    </Row>
                    <Row>
                        {field('Dep Status', cdmInfo?.departureStatus, 2, 8)}
                        {field('Turnaround TTOT', cdmInfo?.turnaroundTtot, 3, 4)}
                        {field('TOBT', cdmInfo?.tobt, 3, 4)}
                    </Row>
                </TitledBorder>
                <TitledBorder title="ARRIVAL (API)" className="custom-titled titled-border">
                    {' '}
                    {/* TODO: Hide if ClientConstants.API_ENABLED is false */}
                    <Row>
                        {field('Impact', arrivalInformation && arrivalInformation.impactSeverityIndicator, 3, 3)}
                        {field('ATV Inbound', arrivalInformation && arrivalInformation.flightStatusInbound, 3, 6)}
                        {field('Registration Mark', arrivalInformation && arrivalInformation.registrationMark, 3, 6)}
                    </Row>
                    <Row>
                        {field(
                            'STAR',
                            arrivalInformation &&
                                AirspaceUtils.toTerminalProcedureOrDctString(arrivalInformation.apiArrivalProcedure),
                            3,
                            3,
                        )}
                        {field(
                            'RWY/TERM',
                            arrivalInformation &&
                                Utils.concat('/', [
                                    arrivalInformation.arrivalRunwayId,
                                    arrivalInformation.arrivalTerminal,
                                ]),
                            3,
                            6,
                        )}
                        {field('IATA ID', arrivalInformation && arrivalInformation.aircraftIataId, 3, 6)}
                    </Row>
                    <Row>
                        {field(
                            'TTO FIX',
                            arrivalInformation &&
                                arrivalInformation.coordinationFix &&
                                arrivalInformation.coordinationFix.id,
                            3,
                            3,
                        )}
                        {field(
                            'TTO',
                            arrivalInformation &&
                                Utils.firstNonNull(
                                    arrivalInformation.consolidatedTargetTimeOver,
                                    arrivalInformation.targetTimeOver,
                                ),
                            3,
                            6,
                        )}
                        {field('Earliest TTO', arrivalInformation && arrivalInformation.earliestTargetTimeOver, 3, 6)}
                    </Row>
                    <Row>
                        {field('Regul', arrivalInformation && arrivalInformation.regulationId, 3, 3)}
                        {field('CTO', arrivalInformation && arrivalInformation.calculatedTimeOver, 3, 6)}
                        {field('IBT', arrivalInformation && arrivalInformation.inBlockTime, 3, 6)}
                    </Row>
                    <Row>
                        {field(
                            'Taxi Time',
                            arrivalInformation && TimeUtils.toDate(arrivalInformation.arrivalTaxiTimeInMs),
                            3,
                            3,
                        )}
                        {field(
                            'MTT',
                            arrivalInformation && TimeUtils.toDate(arrivalInformation.minimumTurnaroundTimeInMs),
                            3,
                            6,
                        )}
                        {field('SIBT', arrivalInformation && arrivalInformation.scheduledInBlockTime, 3, 6)}
                    </Row>
                </TitledBorder>
                <TitledBorder title="ROUTE" className="custom-titled titled-border">
                    <div>{data.icaoRoute || String.fromCharCode(160)}</div>
                </TitledBorder>
                <TitledBorder title="REGULATION" className="custom-titled titled-border">
                    <Row>
                        <Col span={12}>
                            <Row>
                                {field('REGUL+', data.mostPenalisingRegulation, 6, 18)}
                                {field(
                                    'Reg cause',
                                    data.mostPenalizingRegulationCause &&
                                        data.mostPenalizingRegulationCause.reason +
                                            '@' +
                                            data.mostPenalizingRegulationCause.locationCategory,
                                    6,
                                    18,
                                )}
                                {field(
                                    'Delay',
                                    data.delayInMs >= 0 ? data.delayInMs / TimeUtils.MIN_TO_MS : null,
                                    6,
                                    18,
                                )}
                                {field('Last MSG Sent', data.lastAtfmMessageType, 6, 18)}
                            </Row>
                        </Col>
                        <Col span={12}>
                            <Flex style={{ minHeight: '13em' }}>
                                <FlightRegulationLocationTable
                                    data={data.regulationLocations}
                                    columns={[
                                        StandardFlightRegulationLocationColumn.REGULATION,
                                        StandardFlightRegulationLocationColumn.REF_LOCATION,
                                    ]}
                                    columnResizeDisabled={true}
                                    noDataMessage={'No regulations'}
                                    keyProvider={KeyUtils.FLIGHT_REG_LOCATION_KEY_PROVIDER}
                                    sortMode={SortMode.NONE}
                                />
                            </Flex>
                        </Col>
                    </Row>
                </TitledBorder>
            </Flex>
        );
    }

    renderHeader() {
        return (
            <Flex size="2em" className="modal-title header" grow={false} shrink={false}>
                <Flex className="detailed-subpanel-header-left">
                    <div style={{ height: '1em', alignSelf: 'center' }}>
                        <span className="detailed-subpanel-title">FLIGHT DETAILS</span>
                        <span className="detailed-subpanel-title-info">{this.props.id?.flightKeys?.callsign}</span>
                    </div>
                </Flex>
                <Flex className="detailed-subpanel-header-right" style={{ paddingRight: '1.5em' }}>
                    <span>
                        ADEP
                        <Input style={{ width: '5em' }} disabled={true} value={this.props.id?.flightKeys?.adep} />
                    </span>
                    <span>
                        ADES
                        <Input style={{ width: '5em' }} disabled={true} value={this.props.id?.flightKeys?.ades} />
                    </span>
                    <span>
                        IOBT
                        <InputTime style={{ width: '5em' }} value={this.props.id?.flightKeys?.eobt} disabled={true} />
                    </span>
                    <span>
                        PROPOSAL
                        <Checkbox checked={Utils.isExists(this.props.proposalContext)} disabled={true} />
                    </span>
                </Flex>
            </Flex>
        );
    }

    //private methods

    private doGetFlightDetails() {
        if (this.props.id) {
            const request: FlightDetailsRequestDto = {
                clazz: FLIGHT_DETAILS_REQUEST_DTO_CLAZZ_VALUE,
                dataset: this.props.dataset,
                proposalContexts: Utils.isExists(this.props.proposalContext) ? [this.props.proposalContext] : null,
                flightId: this.props.id,
                flightDatasets: [FlightDataset.FLIGHT],
                flightFields: DEFAULT_FLIGHT_FIELDS,
                requestedDataFormat: FlightExchangeModelType.NM_B2B,
                maxWindowForRegulationRiskInMs: -1,
            } as FlightDetailsRequestDto;
            ServiceFactory.coreService()
                .getFlightData(request)
                .then(reply => {
                    if (reply.error) {
                        notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                    } else {
                        this.setState({
                            flightData: reply.data.flight,
                        });
                    }
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                });
        }
    }
}

function field(name: string, value: string | number | boolean | Date, nameSpan: number, valueSpan: number) {
    let displayValue;
    if (value instanceof Date) {
        displayValue = TimeUtils.formatHHMM(value);
    } else if (typeof value === 'boolean') {
        displayValue = value ? 'Y' : 'N';
    } else {
        displayValue = value;
    }
    return (
        <>
            <Col className="fieldName" span={nameSpan}>
                {name}
            </Col>
            <Col span={valueSpan}>{displayValue}</Col>
        </>
    );
}
